import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnConfigEntry } from '@erp/shared-types';
import type { OverrideDisplay } from './overrideDisplay';

/** Saved views of the buyer board's line table (user and org default). */
export const BUYER_BOARD_LINES_TABLE_ID = 'procurement-buyer-board-lines';
/** The transfer board's own saved line views (GEN-3026). */
export const TRANSFER_BOARD_LINES_TABLE_ID = 'procurement-transfer-board-lines';

/** Which board a table belongs to: vendor buys (PO) or transfers (TO). */
export type BoardKind = 'po' | 'to';

export const CALC_COLUMN_ID = 'calcQuantity';
export const QUANTITY_COLUMN_ID = 'quantity';
export const USAGE_12_COLUMN_ID = 'usage12';
export const DEPENDENT_USAGE_COLUMN_ID = 'dependentUsage';
export const SOURCE_AVAILABLE_COLUMN_ID = 'sourceAvailable';
export const AUTO_RELEASE_COLUMN_ID = 'autoRelease';

type Translate = (message: string, options?: Record<string, unknown>) => string;
// The standard layout, the way buyers read the Gains Order Builder grid: the
// item and its description with the stocking and pop codes right after them,
// where and why it is ordered and how much it sold there, then Calc, Qty (the
// quantity that overrides Calc) and the approval state right of Qty. Where the
// line ships from (vendor or a warehouse) sits before the money it changes.
// Money reads buyer cost, revised cost, then the line total and its per-unit
// variance (GEN-3226); the reference columns follow.
const LINE_COLUMN_IDS: readonly string[] = [
  'product',
  'description',
  'stock',
  'pop',
  'warehouse',
  'trigger',
  'designation',
  USAGE_12_COLUMN_ID,
  DEPENDENT_USAGE_COLUMN_ID,
  CALC_COLUMN_ID,
  QUANTITY_COLUMN_ID,
  'approvalState',
  'source',
  SOURCE_AVAILABLE_COLUMN_ID,
  AUTO_RELEASE_COLUMN_ID,
  'unitCost',
  'buyerCost',
  'revisedCost',
  'cost',
  'ppvDelta',
  'contractNumber',
  'ppv',
  'poLineComment',
  'priority',
  'salesOrder',
  'leadTime',
  'buyer',
  'coreOffer',
];

function lineColumnTitles(gt: Translate): Record<string, string> {
  return {
    approvalState: gt('State', {
      context: 'cart line table column header, line approval state',
    }),
    warehouse: gt('Warehouse', { context: 'cart line table column header' }),
    product: gt('Item', { context: 'cart line item column header, the SKU' }),
    description: gt('Description', {
      context: 'cart line table column header, the item description',
    }),
    salesOrder: gt('SO', {
      context: 'cart line table column header, sales order number',
    }),
    [USAGE_12_COLUMN_ID]: gt('12-mo usage', {
      context: 'cart line table column header, customer sales last 12 months',
    }),
    [DEPENDENT_USAGE_COLUMN_ID]: gt('Dependent usage', {
      context:
        'cart line table column header, customer sales of locations that restock from this one',
    }),
    [CALC_COLUMN_ID]: gt('Calc', {
      context: 'cart line table column header, engine calculated quantity',
    }),
    [QUANTITY_COLUMN_ID]: gt('Qty', {
      context: 'cart line table column header',
    }),
    source: gt('From', {
      context: 'cart line table column header, ship-from vendor or location',
    }),
    unitCost: gt('Unit cost', {
      context: 'cart line table column header for buy unit cost',
    }),
    cost: gt('Cost', { context: 'table column header for line cost' }),
    buyerCost: gt('Buyer cost', {
      context: 'cart line table column header, system unit cost',
    }),
    revisedCost: gt('Revised cost', {
      context: 'cart line table column header, one-time special unit cost',
    }),
    ppvDelta: gt('PPV Δ', {
      context:
        'cart line table column header, purchase price variance per unit',
    }),
    contractNumber: gt('Contract #', {
      context: 'cart line table column header, contract number for the PO',
    }),
    ppv: gt('PPV', {
      context:
        'cart line table column header, receive at PO cost or buyer cost',
    }),
    poLineComment: gt('PO comment', {
      context: 'cart line table column header, comment on the PO line',
    }),
    pop: gt('Pop', { context: 'cart line table column header' }),
    stock: gt('Stock', { context: 'cart line table column header' }),
    trigger: gt('Trigger', { context: 'cart line table column header' }),
    designation: gt('Plan', { context: 'cart line table column header' }),
    leadTime: gt('Arrival', { context: 'cart line table column header' }),
    priority: gt('Priority', { context: 'cart line table column header' }),
    buyer: gt('Buyer', { context: 'cart line table column header' }),
    coreOffer: gt('Core offer', {
      context: 'cart line table column header, core-offer program label',
    }),
    [SOURCE_AVAILABLE_COLUMN_ID]: gt('Source avail.', {
      context:
        'cart line table column header, stock the source location can give above its release floor',
    }),
    [AUTO_RELEASE_COLUMN_ID]: gt('Auto release', {
      context:
        'cart line table column header, whether the daily auto-release limit held the line back',
    }),
  };
}

// The PO terms columns start hidden: most lines buy at the system cost, so
// they would widen every board for a few special buys. A buyer shows them from
// the column setup; Buyer cost, Revised cost and PPV Δ show from the start.
// Unit cost starts hidden: Revised cost and Buyer cost already say what the
// line buys at (GEN-3226). Core offer starts hidden too: the Orders row
// carries the count, the label matters only when a buyer reviews core
// material. Trigger starts hidden: buyers read why a line is ordered from Plan
// and the SO number. Dependent usage starts hidden on both boards: the board
// leads with one number, the location's own usage, and its hover carries the
// dependent figure (GEN-3165).
// The transfer columns are blank on a vendor line, so the buyer board hides them.
const HIDDEN_BY_DEFAULT: ReadonlySet<string> = new Set([
  'unitCost',
  'contractNumber',
  'ppv',
  'poLineComment',
  'coreOffer',
  'trigger',
  DEPENDENT_USAGE_COLUMN_ID,
  SOURCE_AVAILABLE_COLUMN_ID,
  AUTO_RELEASE_COLUMN_ID,
]);

// A transfer has no price, so the money columns start hidden there. From
// stays: it names the location the line ships from.
const TRANSFER_HIDDEN_BY_DEFAULT: ReadonlySet<string> = new Set([
  'unitCost',
  'buyerCost',
  'revisedCost',
  'cost',
  'ppvDelta',
  'contractNumber',
  'ppv',
  'poLineComment',
  'coreOffer',
  'trigger',
  DEPENDENT_USAGE_COLUMN_ID,
]);

// The stock flag and pop code start bold so buyers notice them; a buyer can
// turn bold off (or on for any other column) in the column setup.
const BOLD_BY_DEFAULT: ReadonlySet<string> = new Set(['stock', 'pop']);

/**
 * The standard line columns, all sortable. A column starts right-aligned when
 * its definition marks it numeric (`meta.align`).
 */
export function buyerBoardDefaultColumns(
  gt: Translate,
  isNumeric: (id: string) => boolean,
  kind: BoardKind = 'po',
): ColumnConfigEntry[] {
  const titles = lineColumnTitles(gt);
  const hidden = kind === 'po' ? HIDDEN_BY_DEFAULT : TRANSFER_HIDDEN_BY_DEFAULT;
  return LINE_COLUMN_IDS.map((id) => ({
    id,
    apiField: '',
    header: titles[id] ?? id,
    alignRight: isNumeric(id),
    sortable: true,
    ...(hidden.has(id) ? { hidden: true } : {}),
    ...(BOLD_BY_DEFAULT.has(id) ? { style: { bold: true } } : {}),
  }));
}

/**
 * Keep Calc immediately before Qty. Moving Calc carries Qty along; moving
 * anything else (Qty included) leaves Calc in front of Qty.
 */
export function pairCalcWithQuantity(
  columns: ColumnConfigEntry[],
  movedId?: string,
): ColumnConfigEntry[] {
  const calc = columns.find((c) => c.id === CALC_COLUMN_ID);
  const quantity = columns.find((c) => c.id === QUANTITY_COLUMN_ID);
  if (!calc || !quantity) return columns;
  if (movedId === CALC_COLUMN_ID) {
    const rest = columns.filter((c) => c !== quantity);
    const at = rest.indexOf(calc) + 1;
    return [...rest.slice(0, at), quantity, ...rest.slice(at)];
  }
  const rest = columns.filter((c) => c !== calc);
  const at = rest.indexOf(quantity);
  return [...rest.slice(0, at), calc, ...rest.slice(at)];
}

/** A drag in the column list, with Calc and Qty moving as a pair. */
export function moveColumn(
  columns: ColumnConfigEntry[],
  fromIndex: number,
  toIndex: number,
): ColumnConfigEntry[] {
  const movedId = columns[fromIndex]?.id;
  return pairCalcWithQuantity(arrayMove(columns, fromIndex, toIndex), movedId);
}

/** The columns as drawn: in saved order with Calc paired, minus hidden ones. */
export function shownColumns(
  columns: ColumnConfigEntry[],
  hiddenIds: ReadonlySet<string>,
): ColumnConfigEntry[] {
  return pairCalcWithQuantity(columns).filter(
    (column) => !column.hidden && !hiddenIds.has(column.id),
  );
}

const hiddenByView = (columns: readonly ColumnConfigEntry[]) =>
  new Set(columns.flatMap((column) => (column.hidden ? [column.id] : [])));

/** The filterable columns the saved view shows; one it hides is not offered back. */
export function filterColumnsShownByView<Column extends { id: string }>(
  filterColumns: readonly Column[],
  columns: readonly ColumnConfigEntry[],
): Column[] {
  const hidden = hiddenByView(columns);
  return filterColumns.filter((column) => !hidden.has(column.id));
}

/**
 * The line table's one visibility state: the filter-narrowed columns (`false`
 * hidden, `true` shown again), and `false` for every column the saved view
 * hides.
 */
export function lineColumnVisibility(
  columns: readonly ColumnConfigEntry[],
  filterVisibility: Readonly<Record<string, boolean>>,
): Record<string, boolean> {
  const visibility = { ...filterVisibility };
  for (const id of hiddenByView(columns)) visibility[id] = false;
  return visibility;
}

/** A visibility state as a stable string, whatever its key order. */
export function columnVisibilityKey(
  visibility: Readonly<Record<string, boolean>>,
): string {
  return Object.keys(visibility)
    .sort()
    .map((id) => `${id}=${visibility[id] ? 1 : 0}`)
    .join(',');
}

export function visibilityFromKey(key: string): Record<string, boolean> {
  return Object.fromEntries(
    key
      .split(',')
      .filter(Boolean)
      .map((pair) => [pair.slice(0, -2), pair.endsWith('=1')]),
  );
}

/** Widths the buyer dragged columns to, by column id. */
export function columnSizing(
  columns: ColumnConfigEntry[],
): Record<string, number> {
  const sizing: Record<string, number> = {};
  for (const column of columns) {
    if (column.width !== undefined) sizing[column.id] = column.width;
  }
  return sizing;
}

export function withColumnWidths(
  columns: ColumnConfigEntry[],
  sizing: Readonly<Record<string, number>>,
): ColumnConfigEntry[] {
  return columns.map((column) => {
    const width = Math.round(sizing[column.id] ?? 0);
    return width > 0 ? { ...column, width } : column;
  });
}

/** Hiding Calc is the Simple display: the overridden Qty shows Calc on hover. */
export function overrideDisplayOf(
  columns: ColumnConfigEntry[],
): OverrideDisplay {
  return columns.some((c) => c.id === CALC_COLUMN_ID && c.hidden)
    ? 'simple'
    : 'separate';
}
